import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {AuthService} from '@core/services/auth.service';
import {environment} from '@environments/environment';
import {Store} from '@ngxs/store';
import {ClearTokens, SetToken} from '@store/auth/auth.actions';
import {AuthState} from '@store/auth/auth.state';
import {CurrentClientState} from '@store/current-client/current-client.state';
import {TISA_UMPIRE_TOKEN} from '@tisa-group/core/api/umpire/tisa-umpire.providers';
import {WicketTokenData} from '@tisa-group/models/wicket';
import {
  BehaviorSubject,
  catchError,
  filter,
  Observable,
  switchMap,
  take,
  throwError,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  private _isRefreshingToken = false;

  private readonly _refreshTokenSubject$ = new BehaviorSubject<string | null | undefined>(
    null,
  );

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const token = this.store.selectSnapshot(AuthState.token);

    // Do not add authorization header for routes that do not accept it
    if (!this._urlRequiresAuthHeaders(req)) {
      return next.handle(req);
    }
    const reqWithAuthHeaders = this._addAuthHeader(req, token);

    // Handle response
    return next.handle(reqWithAuthHeaders).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401 && token) {
          return this._handleUnauthorizedError(reqWithAuthHeaders, next);
        }

        return throwError(() => error);
      }),
    );
  }

  // Handle error when token expired
  private _handleUnauthorizedError(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this._isRefreshingToken) {
      this._isRefreshingToken = true;
      this._refreshTokenSubject$.next(undefined);

      const refreshToken = this.store.selectSnapshot(AuthState.refreshToken);

      if (refreshToken)
        return this.authService.refreshToken(refreshToken).pipe(
          switchMap(({id_token, refresh_token}: WicketTokenData) => {
            this._isRefreshingToken = false;
            this.store.dispatch(new SetToken(id_token, refresh_token, true));
            this._refreshTokenSubject$.next(id_token);

            const newRequest = this._urlRequiresAuthHeaders(req)
              ? this._addAuthHeader(req, id_token)
              : req;

            return next.handle(newRequest);
          }),
          catchError(error => {
            console.error('Error while refreshing token:', error);

            this.store
              .dispatch(ClearTokens)
              .pipe(take(1))
              .subscribe(() => {
                this._isRefreshingToken = false;
                this._refreshTokenSubject$.next(null);
              });

            return this._refreshTokenSubject$.pipe(
              filter(token => token === null),
              take(1),
              switchMap(() =>
                this._urlRequiresAuthHeaders(req)
                  ? next.handle(
                      req.clone({
                        headers: this._getAuthHeaders(null, this._isUmpireAuthUrl(req)),
                      }),
                    )
                  : next.handle(req),
              ),
            );
          }),
        );
    }

    return this._refreshTokenSubject$.pipe(
      filter(token => token !== undefined),
      take(1),
      switchMap(token => next.handle(this._addAuthHeader(req, token))),
    );
  }

  private _addAuthHeader(
    req: HttpRequest<unknown>,
    token: string | null | undefined,
  ): HttpRequest<unknown> {
    return req.clone({
      headers: this._getAuthHeaders(token, this._isUmpireAuthUrl(req)),
    });
  }

  private _urlRequiresAuthHeaders(req: HttpRequest<unknown>): boolean {
    return this._isBaseAuthUrl(req) || this._isUmpireAuthUrl(req);
  }

  // For Wicket
  private _isBaseAuthUrl(req: HttpRequest<unknown>): boolean {
    const wicketData = this.store.selectSnapshot(CurrentClientState.getData)?.config
      .external_dependencies.wicket;
    if (!wicketData?.url) {
      return false;
    }

    return req.url?.includes(wicketData?.url);
  }

  private _isUmpireAuthUrl(req: HttpRequest<unknown>): boolean {
    return req.url.includes(environment.clientConfig.apiUrls.umpire);
  }

  // Returns auth headers based on api requirements
  private _getAuthHeaders(
    authToken: string | null | undefined,
    isUmpireApi?: boolean,
  ): HttpHeaders {
    return isUmpireApi
      ? new HttpHeaders({
          ['User-Authorization']: authToken || '',
          authorization: this.apiToken(),
        })
      : new HttpHeaders({
          authorization: authToken || '',
        });
  }

  constructor(
    readonly authService: AuthService,
    readonly store: Store,
    @Inject(TISA_UMPIRE_TOKEN) readonly apiToken: (...args: any[]) => string,
  ) {}
}
